import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ArrowLeft, Edit, Plus, FileText, CheckSquare, Calendar, DollarSign, Users, MessageSquare, ExternalLink, Building2 } from "lucide-react";
import { toast } from "sonner";

import { API } from "@/config";

const STAGES = ["Lead", "Discovery", "Proposal", "Negotiation", "Closed - Won", "Closed - Lost"];

// Mock data
const MOCK_OPPORTUNITY = {
  id: "opp-001",
  customer_name: "Whole Foods Market",
  customer_id: "cust-001",
  description: "National distribution deal for organic burritos. This opportunity involves expanding our product line into all Whole Foods locations across the United States.",
  stage: "Negotiation",
  total_revenue: 450000,
  product_type: "Frozen Foods",
  business_type: "Retail",
  close_probability: 75,
  expected_close_date: "2025-02-15",
  created_at: "2024-11-15",
  owner_id: "user-001",
  owner_name: "John Doe",
  broker_id: "broker-001",
  broker_name: "ABC Brokers",
  line_items: [
    { id: "li-001", product_name: "Organic Chicken Burrito", quantity: 50000, unit_price: 4.50, total: 225000 },
    { id: "li-002", product_name: "Veggie Burrito", quantity: 50000, unit_price: 4.00, total: 200000 },
    { id: "li-003", product_name: "Breakfast Burrito", quantity: 10000, unit_price: 2.50, total: 25000 }
  ],
  action_items: [
    { id: "ai-001", title: "Send revised pricing", status: "open", due_date: "2025-01-13", priority: "high", assigned_to: "John Doe" },
    { id: "ai-002", title: "Schedule contract review call", status: "completed", due_date: "2025-01-10", priority: "medium", assigned_to: "Jane Smith" }
  ],
  activities: [
    { id: "act-001", type: "meeting", description: "Call with procurement team to discuss terms", date: "2025-01-10", user: "John Doe" },
    { id: "act-002", type: "email", description: "Sent updated pricing proposal", date: "2025-01-08", user: "John Doe" },
    { id: "act-003", type: "note", description: "Customer requested extended payment terms - 60 days net", date: "2025-01-05", user: "Jane Smith" }
  ],
  related_users: [
    { id: "user-001", name: "John Doe", role: "Account Owner", email: "john@company.com" },
    { id: "user-002", name: "Jane Smith", role: "Sales Support", email: "jane@company.com" }
  ]
};

const OpportunityDetailPage = () => {
  const { opportunityId } = useParams();
  const navigate = useNavigate();
  const [opportunity, setOpportunity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");
  const [showEditModal, setShowEditModal] = useState(false);
  const [showActivityModal, setShowActivityModal] = useState(false);
  const [showActionItemModal, setShowActionItemModal] = useState(false);
  const [editData, setEditData] = useState({});
  const [newActivity, setNewActivity] = useState({ type: "note", description: "" });
  const [newActionItem, setNewActionItem] = useState({ title: "", due_date: "", priority: "medium" });

  useEffect(() => {
    fetchOpportunity();
  }, [opportunityId]);

  const fetchOpportunity = async () => {
    try {
      const response = await axios.get(`${API}/crm/opportunities/${opportunityId}`);
      setOpportunity(response.data);
      setEditData(response.data);
    } catch (error) {
      console.error('Failed to fetch opportunity', error);
      // Use mock data
      setOpportunity(MOCK_OPPORTUNITY);
      setEditData(MOCK_OPPORTUNITY);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateOpportunity = async () => {
    try {
      await axios.put(`${API}/crm/opportunities/${opportunityId}`, editData);
      toast.success("Opportunity updated");
      setShowEditModal(false);
      fetchOpportunity();
    } catch (error) {
      console.error('Failed to update opportunity', error);
      toast.error("Failed to update opportunity");
    }
  };

  const handleAddActivity = async () => {
    try {
      await axios.post(`${API}/crm/activities`, {
        opportunity_id: opportunityId,
        ...newActivity
      });
      toast.success("Activity added");
      setShowActivityModal(false);
      setNewActivity({ type: "note", description: "" });
      fetchOpportunity();
    } catch (error) {
      console.error('Failed to add activity', error);
      toast.error("Failed to add activity");
    }
  };

  const handleAddActionItem = async () => {
    try {
      await axios.post(`${API}/crm/action-items`, {
        opportunity_id: opportunityId,
        ...newActionItem
      });
      toast.success("Action item added");
      setShowActionItemModal(false);
      setNewActionItem({ title: "", due_date: "", priority: "medium" });
      fetchOpportunity();
    } catch (error) {
      console.error('Failed to add action item', error);
      toast.error("Failed to add action item");
    }
  };

  const formatRevenue = (amount) => {
    if (amount >= 1000000) return `$${(amount / 1000000).toFixed(2)}mm`;
    if (amount >= 1000) return `$${(amount / 1000).toFixed(1)}k`;
    return `$${amount?.toFixed(0) || 0}`;
  };

  const getStageColor = (stage) => {
    switch (stage) {
      case 'Lead': return 'bg-blue-100 text-blue-800';
      case 'Discovery': return 'bg-purple-100 text-purple-800';
      case 'Proposal': return 'bg-indigo-100 text-indigo-800';
      case 'Negotiation': return 'bg-orange-100 text-orange-800';
      case 'Closed - Won': return 'bg-green-100 text-green-800';
      case 'Closed - Lost': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'bg-red-100 text-red-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      case 'low': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getActivityIcon = (type) => {
    switch (type) {
      case 'meeting': return '🎤';
      case 'email': return '📧';
      case 'call': return '📞';
      case 'note': return '📝';
      default: return '📌';
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
        </div>
      </div>
    );
  }

  if (!opportunity) {
    return (
      <div className="p-6">
        <Button variant="ghost" onClick={() => navigate('/crm/opportunities')}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Opportunities
        </Button>
        <div className="text-center py-12 text-gray-500">Opportunity not found</div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/crm/opportunities')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <div>
            <div className="flex items-center gap-2">
              <h1 
                className="text-2xl font-bold flex items-center gap-2 cursor-pointer hover:text-indigo-600 transition-colors"
                onClick={() => opportunity.customer_id && navigate(`/crm/customers/${opportunity.customer_id}`)}
              >
                {opportunity.customer_name}
                <ExternalLink className="w-4 h-4 text-gray-400 hover:text-indigo-600" />
              </h1>
              <Badge className={getStageColor(opportunity.stage)}>{opportunity.stage}</Badge>
            </div>
            <p className="text-gray-500 text-sm">{opportunity.description}</p>
          </div>
        </div>
        <Button onClick={() => setShowEditModal(true)}>
          <Edit className="w-4 h-4 mr-2" />
          Edit
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-gray-500 text-sm mb-1">
              <DollarSign className="w-4 h-4" />
              Total Revenue
            </div>
            <div className="text-2xl font-bold text-green-600">{formatRevenue(opportunity.total_revenue)}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-gray-500 text-sm mb-1">
              <DollarSign className="w-4 h-4" />
              Probable Revenue
            </div>
            <div className="text-2xl font-bold text-blue-600">
              {formatRevenue((opportunity.total_revenue || 0) * (opportunity.close_probability || 0) / 100)}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-gray-500 text-sm mb-1">Close Probability</div>
            <div className="text-2xl font-bold">{opportunity.close_probability}%</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-gray-500 text-sm mb-1">
              <Calendar className="w-4 h-4" />
              Expected Close
            </div>
            <div className="text-xl font-bold">
              {opportunity.expected_close_date ? new Date(opportunity.expected_close_date).toLocaleDateString() : 'Not set'}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="line-items">Line Items ({opportunity.line_items?.length || 0})</TabsTrigger>
          <TabsTrigger value="activities">Activities ({opportunity.activities?.length || 0})</TabsTrigger>
          <TabsTrigger value="action-items">Action Items ({opportunity.action_items?.length || 0})</TabsTrigger>
          <TabsTrigger value="team">Team ({opportunity.related_users?.length || 0})</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-4">
          <div className="grid grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Opportunity Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-500">Product Type</span>
                  <span className="font-medium">{opportunity.product_type}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Business Type</span>
                  <span className="font-medium">{opportunity.business_type}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Owner</span>
                  <span className="font-medium">{opportunity.owner_name || 'Unassigned'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Broker</span>
                  <span className="font-medium">{opportunity.broker_name || 'None'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Created</span>
                  <span className="font-medium">{new Date(opportunity.created_at).toLocaleDateString()}</span>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-lg">Recent Activity</CardTitle>
                <Button size="sm" variant="outline" onClick={() => setShowActivityModal(true)}>
                  <Plus className="w-4 h-4 mr-1" />
                  Add
                </Button>
              </CardHeader>
              <CardContent>
                {opportunity.activities?.length === 0 ? (
                  <div className="text-center py-4 text-gray-500">No activities yet</div>
                ) : (
                  <div className="space-y-3 max-h-64 overflow-y-auto">
                    {opportunity.activities?.slice(0, 5).map(activity => (
                      <div key={activity.id} className="flex gap-3 p-2 bg-gray-50 rounded">
                        <span className="text-xl">{getActivityIcon(activity.type)}</span>
                        <div className="flex-1">
                          <p className="text-sm">{activity.description}</p>
                          <p className="text-xs text-gray-500">{activity.user} • {new Date(activity.date).toLocaleDateString()}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="line-items" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Line Items</CardTitle>
            </CardHeader>
            <CardContent>
              {opportunity.line_items?.length === 0 ? (
                <div className="text-center py-8 text-gray-500">No line items</div>
              ) : (
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-2">Product</th>
                      <th className="text-right py-2">Quantity</th>
                      <th className="text-right py-2">Unit Price</th>
                      <th className="text-right py-2">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {opportunity.line_items?.map(item => (
                      <tr key={item.id} className="border-b">
                        <td className="py-2">{item.product_name}</td>
                        <td className="text-right py-2">{item.quantity?.toLocaleString()}</td>
                        <td className="text-right py-2">${item.unit_price?.toFixed(2)}</td>
                        <td className="text-right py-2 font-medium">{formatRevenue(item.total)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="font-bold">
                      <td colSpan="3" className="text-right py-2">Total:</td>
                      <td className="text-right py-2 text-green-600">{formatRevenue(opportunity.total_revenue)}</td>
                    </tr>
                  </tfoot>
                </table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="activities" className="mt-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg">Activity Timeline</CardTitle>
              <Button size="sm" onClick={() => setShowActivityModal(true)}>
                <Plus className="w-4 h-4 mr-1" />
                Add Activity
              </Button>
            </CardHeader>
            <CardContent>
              {opportunity.activities?.length === 0 ? (
                <div className="text-center py-8 text-gray-500">No activities recorded</div>
              ) : (
                <div className="space-y-4">
                  {opportunity.activities?.map(activity => (
                    <div key={activity.id} className="flex gap-4 p-3 bg-gray-50 rounded-lg">
                      <span className="text-2xl">{getActivityIcon(activity.type)}</span>
                      <div className="flex-1">
                        <p className="font-medium">{activity.description}</p>
                        <p className="text-sm text-gray-500 mt-1">
                          {activity.user} • {new Date(activity.date).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="action-items" className="mt-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg">Action Items</CardTitle>
              <Button size="sm" onClick={() => setShowActionItemModal(true)}>
                <Plus className="w-4 h-4 mr-1" />
                Add Action Item
              </Button>
            </CardHeader>
            <CardContent>
              {opportunity.action_items?.length === 0 ? (
                <div className="text-center py-8 text-gray-500">No action items</div>
              ) : (
                <div className="space-y-3">
                  {opportunity.action_items?.map(item => (
                    <div key={item.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <CheckSquare className={`w-5 h-5 ${item.status === 'completed' ? 'text-green-500' : 'text-gray-400'}`} />
                        <div>
                          <p className={`font-medium ${item.status === 'completed' ? 'line-through text-gray-400' : ''}`}>
                            {item.title}
                          </p>
                          <p className="text-xs text-gray-500">
                            Due: {new Date(item.due_date).toLocaleDateString()} • {item.assigned_to}
                          </p>
                        </div>
                      </div>
                      <Badge className={getPriorityColor(item.priority)}>{item.priority}</Badge>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="team" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Team Members</CardTitle>
            </CardHeader>
            <CardContent>
              {opportunity.related_users?.length === 0 ? (
                <div className="text-center py-8 text-gray-500">No team members assigned</div>
              ) : (
                <div className="space-y-3">
                  {opportunity.related_users?.map(user => (
                    <div key={user.id} className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg">
                      <div className="w-10 h-10 bg-indigo-100 rounded-full flex items-center justify-center">
                        <span className="text-indigo-600 font-medium">
                          {user.name?.split(' ').map(n => n[0]).join('')}
                        </span>
                      </div>
                      <div>
                        <p className="font-medium">{user.name}</p>
                        <p className="text-sm text-gray-500">{user.role} • {user.email}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Edit Opportunity Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit Opportunity</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Stage</Label>
              <Select value={editData.stage} onValueChange={(val) => setEditData({ ...editData, stage: val })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STAGES.map(stage => (
                    <SelectItem key={stage} value={stage}>{stage}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Close Probability (%)</Label>
              <Input
                type="number"
                min="0"
                max="100"
                value={editData.close_probability}
                onChange={(e) => setEditData({ ...editData, close_probability: parseInt(e.target.value) || 0 })}
              />
            </div>
            <div>
              <Label>Expected Close Date</Label>
              <Input
                type="date"
                value={editData.expected_close_date}
                onChange={(e) => setEditData({ ...editData, expected_close_date: e.target.value })}
              />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea
                value={editData.description}
                onChange={(e) => setEditData({ ...editData, description: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditModal(false)}>Cancel</Button>
            <Button onClick={handleUpdateOpportunity}>Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Activity Modal */}
      <Dialog open={showActivityModal} onOpenChange={setShowActivityModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Activity</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Type</Label>
              <Select value={newActivity.type} onValueChange={(val) => setNewActivity({ ...newActivity, type: val })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="note">📝 Note</SelectItem>
                  <SelectItem value="meeting">🎤 Meeting</SelectItem>
                  <SelectItem value="call">📞 Call</SelectItem>
                  <SelectItem value="email">📧 Email</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Description</Label>
              <Textarea
                value={newActivity.description}
                onChange={(e) => setNewActivity({ ...newActivity, description: e.target.value })}
                placeholder="Describe the activity..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowActivityModal(false)}>Cancel</Button>
            <Button onClick={handleAddActivity} disabled={!newActivity.description}>Add Activity</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Action Item Modal */}
      <Dialog open={showActionItemModal} onOpenChange={setShowActionItemModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Action Item</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Title</Label>
              <Input
                value={newActionItem.title}
                onChange={(e) => setNewActionItem({ ...newActionItem, title: e.target.value })}
                placeholder="What needs to be done?"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Due Date</Label>
                <Input
                  type="date"
                  value={newActionItem.due_date}
                  onChange={(e) => setNewActionItem({ ...newActionItem, due_date: e.target.value })}
                />
              </div>
              <div>
                <Label>Priority</Label>
                <Select value={newActionItem.priority} onValueChange={(val) => setNewActionItem({ ...newActionItem, priority: val })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowActionItemModal(false)}>Cancel</Button>
            <Button onClick={handleAddActionItem} disabled={!newActionItem.title}>Add Action Item</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OpportunityDetailPage;
